<template>
  <div class="min-w-0 flex-1">
    <SettingsPageFull>
      <!--region header-->
      <template #title>
        <div class="flex space-x-3">
          <span class="text-3xl font-bold capitalize-first">{{
            $t('navigation.content_supplier_title')
          }}</span>
        </div>
      </template>

      <template #cta>
        <BaseButton
          v-if="$can('manage_content')"
          color="primary"
          :title="$t('button.add_new_m', { var: $t('attributes.supplier') })"
          icon="plus"
          @click="$router.push({ name: 'settings_create_supplier_route' })"
        />
      </template>
      <!--endregion-->

      <!--region body-->
      <template #body>
        <BaseTableCardsComponent
          v-if="filteredEntries && metadata"
          :entries="filteredEntries"
          :table-metadata="metadata"
          :with-pagination="true"
          base-card-title="name"
          :storage-name="'dt-supplier-index'"
          :select-entry="
            (supplier) =>
              $router.push({
                name: 'settings_edit_supplier_route',
                params: { supplierId: supplier.id }
              })
          "
          :actions="cActions"
          :available-filter="metadata.filters"
          @update:filters="handleUpdateFilters"
        >
          <template #table-cell="{ index, data: supplier, column }">
            <td
              v-if="index === 0"
              class="top-0 z-10 border-b border-gray-200/75 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            >
              <div class="flex flex-row items-center gap-2">
                <div>
                  <router-link
                    :to="{
                      name: 'settings_edit_supplier_route',
                      params: { supplierId: supplier.id }
                    }"
                    class="font-bold"
                  >
                    <div class="capitalize-first">{{ supplier.name }}</div>
                  </router-link>
                </div>
              </div>
            </td>
          </template>
          <template #card-details="{ entry: supplier }">
            <div class="flex-gap-1 px-5 pb-5 text-center lg:text-left">
              <div
                v-if="supplier.email"
                class="flex cursor-default items-center justify-center text-gray-600 lg:justify-start"
              >
                <MailIcon class="mr-2 h-3 w-3" />
                {{ supplier.email }}
              </div>
              <div
                v-if="supplier.phone_field"
                class="flex cursor-default items-center justify-center text-gray-600 lg:justify-start"
              >
                <PhoneIcon class="mr-2 h-3 w-3" />
                <BaseShowPhoneLabel
                  :country-value="supplier.phone_country"
                  :model-value="supplier.phone_field"
                />
              </div>
              <div
                v-if="supplier.full_address"
                class="flex cursor-default items-center justify-center text-gray-600 lg:justify-start"
              >
                <FontAwesomeIcon
                  :icon="['fal', 'location-arrow']"
                  size="xs"
                  class="mr-2"
                />
                {{ supplier.full_address }}
              </div>
            </div>
          </template>
        </BaseTableCardsComponent>
      </template>
      <!--endregion-->
    </SettingsPageFull>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import SettingsPageFull from '../../../../../components/SettingsPageFull'
import BaseTableCardsComponent from '@c/BaseTableCardsComponent'
import BasePaginationMixin from '@u/mixins/Pagination/BasePaginationMixin'
import BaseTableCardsFilterMixin from '@u/mixins/Filter/BaseTableCardsFilterMixin'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseShowPhoneLabel from '@c/addf-package/components/BaseLabel/BaseShowPhoneLabel.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'TheSupplierIndexPage',
  components: {
    FontAwesomeIcon,
    BaseShowPhoneLabel,
    BaseButton,
    BaseTableCardsComponent,
    SettingsPageFull
  },
  mixins: [BasePaginationMixin, BaseTableCardsFilterMixin],
  data() {
    return {
      filterText: ''
    }
  },
  computed: {
    ...mapGetters({
      entries: 'supplier/getSupplierList',
      metadata: 'supplier/getMetadata'
    }),
    cActions() {
      const actions = [
        {
          label: this.$t('global.historic'),
          icon: 'clock-rotate-left',
          bgColor: 'bg-gradient-to-b from-gray-100 to-gray-200',
          fgColor: 'text-gray-700',
          clickFunction: (supplier) =>
            this.$router.push({
              name: 'settings_edit_supplier_history_route',
              params: { supplierId: supplier.id }
            })
        }
      ]

      if (this.$can('manage_content')) {
        actions.push({
          label: this.$t('button.edit'),
          icon: 'edit',
          bgColor: 'bg-gradient-to-b from-gray-100 to-gray-200',
          fgColor: 'text-gray-700',
          clickFunction: (supplier) =>
            this.redirectToEditableModelPage(supplier.id)
        })
      }

      if (this.$can('delete_content')) {
        actions.push({
          label: this.$t('button.delete'),
          icon: 'trash',
          bgColor: 'bg-gradient-to-b from-red-600 to-red-700',
          fgColor: 'text-white',
          clickFunction: (supplier) => this.deleteSupplier(supplier.id)
        })
      }

      return actions
    }
  },
  mounted() {
    this.processFetchSupplierListAction()
    this.processSetEditionModeMutation(false)
  },
  methods: {
    ...mapActions({
      processFetchSupplierListAction: 'supplier/fetchSupplierList',
      processDeleteSupplierByIdAction: 'supplier/deleteSupplierById',
      processUndoDeleteSupplierByIdAction: 'supplier/undoDeleteSupplierById'
    }),
    ...mapMutations({
      processSetEditionModeMutation: 'supplier/setEditionMode'
    }),
    redirectToModelPage(id) {
      this.$router.push({
        name: 'settings_edit_supplier_route',
        params: { supplierId: id }
      })
    },
    redirectToEditableModelPage(id) {
      if (this.$can('manage_content')) {
        this.processSetEditionModeMutation(true)
      }
      this.$router.push({
        name: 'settings_edit_supplier_route',
        params: { supplierId: id }
      })
    },
    deleteSupplier(id) {
      this.processDeleteSupplierByIdAction({
        id: id
      }).then((response) => {
        if (response.success) {
          this.processFetchSupplierListAction()
          this.restoreSupplier(response.deleted_id)
        }
      })
    },
    restoreSupplier(id) {
      this.$h.toastAction(
        'delete',
        this.$t('toasts.this_item_has_been_removed'),
        null,
        [
          {
            title: this.$t('button.cancel'),
            class: 'text-red-700',
            action: () => {
              // process de la restauration
              this.processUndoDeleteSupplierByIdAction({
                id: id
              }).then((response) => {
                this.processFetchSupplierListAction()
                this.$h.toastSuccess(
                  this.$t('toasts.reseted_m', {
                    var: this.$t('attributes.supplier')
                  })
                )
              })
            }
          }
        ]
      )
    }
  }
}
</script>
